import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input, { Textarea } from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddTripTypeForm from "./useAddTripTypeForm";

export const addTripTypePageRouteName = "/config/trip-type/add";

const AddTripTypePage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    t,
  } = useAddTripTypeForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-trip-type-name")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                placeholder={t("label-trip-type-name")}
                required
                {...register("tripTypeName")}
                error={errors.tripTypeName}
                data-testid="input-trip-type-name"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-description")}
              </Typography>
              <Textarea
                placeholder={t("label-description")}
                {...register("description")}
                error={errors.description}
                data-testid="textarea-trip-type-description"
              />
            </div>

            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-trip-type-status"
                  />
                </div>
              )}
            />

            <Controller
              name="isFreezer"
              control={control}
              render={({ field }) => (
                <div className="flex items-center justify-between">
                  <Typography variant="bodyLarge">
                    {t("label-is-freezer")}
                  </Typography>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={field.value}
                    onClick={() => field.onChange(!field.value)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      field.value ? "bg-primary-600" : "bg-greyScale-60"
                    }`}
                    data-testid="toggle-is-freezer-trip-type"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        field.value ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              )}
            />

            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-trip-type"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-trip-type"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-to-list-trip-type"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {fields.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between rounded-xl border p-4 ${
                    editingIndex === index
                      ? "border-primary-500 bg-primary-50"
                      : "border-border"
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <Typography variant="bodyLarge" className="font-semibold">
                      {item.tripTypeName}
                    </Typography>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-description")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography
                        variant="body"
                        tone="muted"
                      >
                        {item.description || "-"}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-status")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography
                        variant="body"
                        className="capitalize"
                        tone="muted"
                      >
                        {item.status}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-is-freezer")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography variant="body" tone="muted">
                        {item.isFreezer ? "Yes" : "No"}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <button
                      type="button"
                      onClick={() => handleEditItem(index)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer border ${
                        editingIndex === index
                          ? "bg-primary-500 text-white border-primary-500"
                          : "hover:bg-greyScale-90 text-primary-500 border-primary-500"
                      }`}
                      data-testid={`btn-edit-row-trip-type-${index}`}
                    >
                      <Icon name="edit-2" size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(index)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer border border-error-500"
                      data-testid={`btn-delete-row-trip-type-${index}`}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-trip-type"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-trip-type"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="btn-save-confirmation-trip-type"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title")}
        message={t("modal-cancel-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="btn-cancel-confirmation-trip-type"
      />
    </div>
  );
};

export default AddTripTypePage;
